import { useEffect, useRef } from 'react';
import styles from './choice.module.css';
import { Col, Row, Table } from 'antd';
import Totals from './_Totals';
import { Colors } from '../../../core/Theme';
import { Bar, Pie } from '@ant-design/charts';
import { memo } from 'react';
import { formatNumber, formatPercentage } from '../../../../components/Percent';
import useWindowSize from '../../../../components/useWindowResize';

const COLORS = [
    Colors.charts.greenish,
    Colors.charts.gray,
    Colors.charts.green,
    Colors.charts.turquoise,
];

const Choice = ({ data, question }: { data: any; question: any }) => {
    let chart1Ref = useRef<any>(null);
    let chart2Ref = useRef<any>(null);
    let chart1ContainerRef = useRef<any>(null);
    let chart2ContainerRef = useRef<any>(null);

    const size = useWindowSize();
    const colorKeys: string[] = [
        ...Object.keys(data.aggregated)
            .filter((k) => k[0] !== '_')
            .sort(
                (a, b) =>
                    (data.aggregated[a]?.order || 0) -
                    (data.aggregated[b]?.order || 0),
            ),
        ...Array.from(
            new Set<string>(
                data.consolidated.map((item: any) => String(item.type)),
            ),
        ),
    ];

    const keyColorMap = colorKeys.reduce<Record<string, string>>(
        (acc, key, index) => ({
            ...acc,
            [key]: COLORS[index % COLORS.length],
        }),
        {},
    );

    const getColorForKey = (key: string) => keyColorMap[key] || COLORS[0];

    useEffect(() => {
        if (chart2Ref && chart2Ref.current) {
            try {
                const currentWidth =
                    chart2ContainerRef?.current?.offsetWidth || undefined;
                chart2Ref.current
                    .getChart()
                    .changeSize(
                        currentWidth - 24,
                        (data.consolidated.length /
                            Object.keys(data.aggregated).filter(
                                (k) => k[0] !== '_',
                            ).length) *
                            32,
                    );
            } catch (err) {}
        }
        if (chart1Ref && chart1Ref.current) {
            const currentWidth =
                chart1ContainerRef?.current?.offsetWidth || undefined;
            chart1Ref.current.getChart().changeSize(currentWidth, 300);
            chart1Ref.current.getChart().update({
                ...chart1Ref.current.getChart().options,
                legend: {
                    ...chart1Ref.current.getChart().options.legend,
                    offsetX: (Math.max(currentWidth - 400, 0) / 3) * -1,
                },
            });
        }
    }, [size, data]);

    return (
        <div className={styles.container}>
            <Row>
                <Col
                    md={24}
                    lg={12}
                    style={{ paddingLeft: 0 }}
                    ref={chart1ContainerRef}
                >
                    <div className={styles.chartContanier}>
                        <Pie
                            ref={chart1Ref}
                            autoFit={true}
                            data={Object.keys(data.aggregated)
                                .filter((k) => k[0] !== '_')
                                .map((k) => ({
                                    ...data.aggregated[k],
                                    id: k,
                                    value: data.aggregated[k].total,
                                }))
                                .sort((a, b) => b.order - a.order)}
                            angleField="percentage"
                            colorField="id"
                            innerRadius={0.5}
                            color={(datum: any) =>
                                getColorForKey(datum.id || datum.type)
                            }
                            tooltip={false}
                            legend={{
                                label: {
                                    style: {
                                        fontSize: 11,
                                        fontFamily: '"Montserrat", sans-serif',
                                        fill: Colors.black,
                                    },
                                },
                            }}
                            label={{
                                type: 'inner',
                                offset: '-50%',
                                autoRotate: false,
                                content: (data) =>
                                    formatPercentage(data.percentage, 2),
                                style: {
                                    fontSize: 12,
                                    fontFamily: '"Montserrat", sans-serif',
                                    fill: Colors.black,
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                },
                            }}
                            statistic={{ title: false, content: false }}
                        />
                    </div>
                </Col>
                <Col md={24} lg={12}>
                    <h3>Promedios</h3>
                    <div
                        style={{ height: 300, overflowY: 'auto' }}
                        ref={chart2ContainerRef}
                    >
                        <Bar
                            data={data.consolidated
                                .map((c: any) => ({ ...c, value: c.total }))
                                .sort((a: any, b: any) => a.order - b.order)}
                            ref={chart2Ref}
                            yField="text"
                            xField={
                                question.type !== 'YesNo' ? 'total' : 'average'
                            }
                            seriesField="type"
                            height={
                                (data.consolidated.length /
                                    Object.keys(data.aggregated).filter(
                                        (k) => k[0] !== '_',
                                    ).length) *
                                32
                            }
                            legend={false}
                            autoFit={false}
                            isPercent={question.type !== 'YesNo'}
                            isStack
                            tooltip={{
                                title: (title, datum) =>
                                    `${datum.text}: ${formatNumber(
                                        data.consolidated
                                            .filter(
                                                (c: any) =>
                                                    c.text === datum.text,
                                            )
                                            .reduce(
                                                (prev: number, next: any) =>
                                                    prev + next.total,
                                                0,
                                            ),
                                        0,
                                    )}`,
                                formatter: (datum) => ({
                                    name:
                                        datum.type.length > 30
                                            ? datum.type.substring(0, 27) +
                                              '...'
                                            : datum.type,
                                    value: formatPercentage(
                                        question.type === 'YesNo'
                                            ? datum.average
                                            : datum.total,
                                        2,
                                    ),
                                }),
                            }}
                            label={{
                                style: {
                                    fill: Colors.black,
                                    fontFamily: '"Montserrat", sans-serif',
                                    fontWeight: 'bold',
                                    fontSize: 10,
                                    textAlign: 'center',
                                },
                                position: 'middle',
                                content: (d) =>
                                    (question.type === 'YesNo'
                                        ? d.average
                                        : d.total) > 0.2
                                        ? formatPercentage(
                                              question.type === 'YesNo'
                                                  ? d.average
                                                  : d.total,
                                              1,
                                          )
                                        : '',
                            }}
                            xAxis={{ grid: null, label: null }}
                            yAxis={{
                                grid: null,
                                line: null,
                                label: {
                                    autoEllipsis: true,
                                    formatter: (str) =>
                                        str.length > 30
                                            ? str.substring(0, 27) + '...'
                                            : str,
                                    style: {
                                        fontSize: 11,
                                        fontFamily: '"Montserrat", sans-serif',
                                        fill: Colors.black,
                                        fontWeight: 'normal',
                                    },
                                },
                            }}
                            color={(datum: any) =>
                                getColorForKey(datum.type || datum.id)
                            }
                        />
                    </div>
                </Col>
            </Row>

            <Totals data={data} showAverage={false} />
            {!!data.aggregated._others && (
                <Table
                    size="small"
                    className={styles.tbl}
                    columns={[
                        {
                            title: data.aggregated._othersLabel,
                            dataIndex: 'k',
                            key: 'k',
                            width: '80%',
                        },
                        {
                            title: 'Total',
                            dataIndex: 'total',
                            key: 'total',
                            align: 'right',
                        },
                        {
                            title: '% (Porc.)',
                            dataIndex: 'percentage',
                            key: 'percentage',
                            align: 'right',
                            render: (v) =>
                                `${Number(v * 100)
                                    .toFixed(2)
                                    .replace(/[.,]00$/, '')}%`,
                        },
                    ]}
                    dataSource={Object.keys(data.aggregated._others || {})
                        .map((k) => ({ ...data.aggregated._others[k], k }))
                        .sort((a: any, b: any) => b.total - a.total)}
                    rowKey="k"
                    pagination={false}
                    scroll={{ y: 197 }}
                    style={{ margin: 32 }}
                />
            )}
        </div>
    );
};

export default memo(Choice);
